import { useAppDispatch, useAppSelector } from 'hooks/useStore'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { FactureErrorState } from '@Arti-zen/package-backoffice'
import { factureListActions } from '../../store/store.facturation'
import { Format } from 'utils/format'
import { DatePicker } from '@mui/x-date-pickers'
import { Button, Checkbox, FormControlLabel, TextField } from '@mui/material'
import { EPaymentProgressStatus, FactureExtended } from '../../store/store.facturation.interfaces'
import CsvDownloadButton from 'react-json-to-csv'
import { Wrapper, LeftBar, WrapperFilter, WrapperCheckBoxes, WrapperContent, Title } from './FactureList.styled'
import FactureList from 'components/FactureList/FactureList'
import { useDebounce } from 'hooks/useDebounce'
import { DateTime } from 'luxon'

export default function FactureListHome(): ReactElement {
  const dispatch = useAppDispatch()
  const {
    displayedFactures,
    dateStart,
    dateEnd,
    errorList,
    deactivatedClient,
    activatedClient,
    paymentStatusList,
    isRefreshing,
  } = useAppSelector((state) => state.facturation)
  const [search, setSearch] = useState<string>()
  const textSearched = useDebounce(search, 1000)

  useEffect(() => {
    dispatch(factureListActions.setSearch(textSearched))
  }, [textSearched])

  const rateLimitedGetList = useMemo(() => {
    return displayedFactures.filter((fact) => fact.paymentProgress === EPaymentProgressStatus.error_rate_limit)
  }, [displayedFactures])

  const retryRateLimited = useCallback(() => {
    rateLimitedGetList.forEach((fact) => dispatch(factureListActions.generatePaymentGCL(fact.id)))
  }, [rateLimitedGetList])

  return (
    <Wrapper>
      <LeftBar>
        <DatePicker
          label="Date de début"
          value={dateStart}
          onChange={(e) => dispatch(factureListActions.setDateStart(e))}
          timezone="Europe/Paris"
        />

        <DatePicker label="Date de fin" value={dateEnd} onChange={(e) => dispatch(factureListActions.setDateEnd(e))} />

        <WrapperFilter>
          <Title>Génération de la facture</Title>
          <WrapperCheckBoxes>
            {Object.values(FactureErrorState).map((error) => {
              return (
                <FormControlLabel
                  key={error}
                  control={
                    <Checkbox
                      onChange={() => dispatch(factureListActions.switchError(error))}
                      checked={errorList.includes(error)}
                      size="small"
                    />
                  }
                  label={`${error} (${displayedFactures.filter((fact) => fact.errorState === error).length})`}
                  labelPlacement="end"
                  componentsProps={{ typography: { style: { fontSize: 14 } } }}
                />
              )
            })}
          </WrapperCheckBoxes>
        </WrapperFilter>

        <WrapperFilter>
          <Title>Statut du paiement</Title>
          <WrapperCheckBoxes>
            {Object.values(EPaymentProgressStatus).map((state) => {
              return (
                <FormControlLabel
                  key={state}
                  control={
                    <Checkbox
                      onChange={() => dispatch(factureListActions.switchPaymentStatus(state))}
                      checked={paymentStatusList.includes(state)}
                      size="small"
                    />
                  }
                  label={labelPaymentStatus(displayedFactures, state)}
                  labelPlacement="end"
                  componentsProps={{ typography: { style: { fontSize: 14 } } }}
                />
              )
            })}
          </WrapperCheckBoxes>
        </WrapperFilter>
        <WrapperFilter>
          <Title>Etat du compte artisan</Title>
          <WrapperCheckBoxes>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={() => dispatch(factureListActions.switchDeactivatedClient(!deactivatedClient))}
                  checked={deactivatedClient}
                  size="small"
                />
              }
              label={`bloqué`}
              labelPlacement="end"
              componentsProps={{ typography: { style: { fontSize: 14 } } }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  onChange={() => dispatch(factureListActions.switchActivatedClient(!activatedClient))}
                  checked={activatedClient}
                  size="small"
                />
              }
              label={`Non bloqué`}
              labelPlacement="end"
              componentsProps={{ typography: { style: { fontSize: 14 } } }}
            />
          </WrapperCheckBoxes>
        </WrapperFilter>

        <CsvDownloadButton
          data={displayedFactures.map((fact) => {
            const [_artisanId, annee, mois] = fact.internalRef?.split('_')
            const promo = fact.detail?.promo
            const totalBeforePromo = Number(fact.detail.mainSubscription + fact.detail.options + fact.detail.subAccounts + fact.detailFull.totalVarHT) / 100
            const totalBeforePromoNoPayment = Number(fact.detail.mainSubscription + fact.detail.options + fact.detail.subAccounts) / 100
            const promoTotalHT = Number(fact.detailFull.totalPromoHT) / 100
            const promoTotalPercent = (promoTotalHT / totalBeforePromoNoPayment * 100)

            const numFormat = (val: number | string) => Number(val).toFixed(2).replace('.', ',')

            return {
              refInterne: fact.internalRef,
              refFactPro: fact.ref,
              annee,
              mois,
              clientId: fact.clientId,
              email: fact.client?.email,
              amount: numFormat(fact.amount),
              amountTTC: numFormat(fact.amountTTC),
              status: fact.paymentProgress,
              paymentGCL: fact.paymentGCL?.GCLid || fact.paymentRef,
              customerGCL: fact.gocardlessClientID,
              createdAt: DateTime.fromJSDate(new Date(fact.createdAt)).toFormat('dd/MM/yyyy'),
              bloqué: fact.client?.isDisactivated,
              forfait: numFormat(((fact.detail?.mainSubscription ?? 0) + (fact.detail?.options ?? 0)) / 100),
              subAccounts: numFormat(fact.detail.subAccounts / 100),
              payments: numFormat(fact.detail?.paymentFeesHT / 100),
              totalBeforePromo: numFormat(totalBeforePromo),
              promoTotalHT: numFormat(promoTotalHT),
              promoTotalPercentPaymentExcluded: numFormat(promoTotalPercent),
              promoType: promo?.type,
            }
          })}
          title="Factures"
          filename={`Factures du ${dateStart.toFormat('yyyy/MM/dd')} au ${dateEnd.toFormat('yyyy/MM/dd')}`}
          style={{ margin: 5 }}
        >
          Télécharger
        </CsvDownloadButton>
      </LeftBar>

      <WrapperContent>
        <TextField
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher une facture (artisanId, email, numéro de facture)"
          style={{ display: 'flex', width: '50%' }}
        />
        <div
          style={{
            marginBottom: 10,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>
            {displayedFactures.length} factures pour{' '}
            {Format.currency(
              displayedFactures.reduce((acc, fact) => acc + +fact.amount, 0),
              2
            )}{' '}
            HT
            <span style={{ fontSize: 14, fontStyle: 'italic' }}>
              {' '}
              (
              {Format.currency(
                displayedFactures.reduce((acc, fact) => acc + +fact.amountTTC, 0),
                2
              )}{' '}
              TTC)
            </span>
          </span>

          <Button onClick={() => dispatch(factureListActions.getList())} variant="contained" disabled={isRefreshing}>
            {isRefreshing ? '...' : 'Refresh'}
          </Button>

          {rateLimitedGetList?.length > 0 && (
            <Button onClick={retryRateLimited} variant="contained" color="warning" disabled={isRefreshing}>
              {isRefreshing ? '...' : `Relancer RL (${rateLimitedGetList?.length})`}
            </Button>
          )}
        </div>

        <FactureList
          factureList={displayedFactures}
          updatePayment={(id: string) => dispatch(factureListActions.updateFacturePayment(id))}
          generatePaymentGCL={(id: string) => dispatch(factureListActions.generatePaymentGCL(id))}
        />
      </WrapperContent>
    </Wrapper>
  )
}

const labelPaymentStatus = (displayedFactures: FactureExtended[], paymentProgressStatus: EPaymentProgressStatus) => {
  const list = displayedFactures.filter((fact) => fact.paymentProgress === paymentProgressStatus)
  const count = list.length
  const totalTTC = list.reduce((acc, fact) => acc + +fact.amountTTC, 0)

  return `${paymentProgressStatus}: ${Format.currency(totalTTC, 2)} (${count})`
}
